import { useMutation } from '@apollo/client';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  InsertChildControlDocument,
  LinkItemsDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { Plus } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { evictField, toLocalDate } from 'src/utils';

import { AISidePanelHeader } from '@/components/side-panel/implementations/ai/AISidePanelHeader';
import { AISidePanelLoading } from '@/components/side-panel/implementations/ai/AISidePanelLoading';
import { AISuggestedControlCard } from '@/components/side-panel/implementations/ai/assistance/suggested-risk-controls/AISuggestedControlCard';
import { useAISuggestedRiskControlsStore } from '@/components/side-panel/implementations/ai/assistance/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { SidePanelContainer } from '@/components/side-panel/SidePanelContainer';
import { useSidePanelStore } from '@/components/side-panel/useSidePanelStore';
import { handleError } from '@/utils/errorUtils';

interface AISuggestedRiskControlProps {
  onClose: () => void;
}

export const AISuggestedRiskControls: FC<AISuggestedRiskControlProps> = ({
  onClose,
}) => {
  const { result, reset, isJobInProgress, riskId, hasErrored, hasTimedOut } =
    useAISuggestedRiskControlsStore();

  const { close: closeSidePanel, onActionCompleted } = useSidePanelStore();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addingControls, setAddingControls] = useState<boolean>();

  const { addNotification } = useNotifications();

  const { user } = useRisksmartUser();

  const [linkControlToRisk] = useMutation(LinkItemsDocument, {
    update: (cache) => {
      evictField(cache, 'linked_item');
      evictField(cache, 'control');
      evictField(cache, 'action');
      evictField(cache, 'issue');
      evictField(cache, 'appetite');
      evictField(cache, 'risk');
    },
  });

  const [addNewControl] = useMutation(InsertChildControlDocument, {
    update: (cache) => {
      evictField(cache, 'risk');
      evictField(cache, 'control');
      evictField(cache, 'control_aggregate');
      evictField(cache, 'obligation');
      evictField(cache, 'linked_item');
      evictField(cache, 'risk_score');
    },
  });

  async function linkSelectedControls(): Promise<void> {
    const controlsToLink = result!.result!.suggestedControls.filter(
      (ctrl) => selectedIds.includes(ctrl.controlId!) && ctrl.isExisting
    );

    if (controlsToLink.length > 0) {
      await linkControlToRisk({
        variables: {
          Source: riskId!,
          Targets: controlsToLink.map((item) => item.controlId!),
        },
      });
    }
  }

  async function addNewControls(): Promise<void> {
    const controlsToAdd = result!.result!.suggestedControls.filter(
      (ctrl) => selectedIds.includes(ctrl.controlId!) && !ctrl.isExisting
    );

    for (const controlToAdd of controlsToAdd) {
      await addNewControl({
        variables: {
          object: {
            Title: controlToAdd.title,
            Description: controlToAdd.description,
            Type: controlToAdd.controlType,
            ParentId: riskId!,
            DepartmentTypeIds: [],
            OwnerUserIds: [user!.userId],
            OwnerGroupIds: [],
            ContributorGroupIds: [],
            ContributorUserIds: [],
            TagTypeIds: [],
            CustomAttributeData: undefined,
            schedule: {
              Frequency: undefined,
              ManualDueDate: undefined,
              StartDate: undefined,
              TimeToCompleteUnit: undefined,
              TimeToCompleteValue: undefined,
            },
          },
        },
      });
    }
  }

  function validateRiskIdAndUser(): void {
    if (!riskId) {
      throw new Error(
        'Attempting to add/link controls from the AI suggestions, but the parent RiskId has not been set'
      );
    }

    if (!user) {
      throw new Error(
        'Attempting to add/link controls from the AI suggestions, but there is no user set'
      );
    }
  }

  async function addControls(): Promise<void> {
    setAddingControls(true);

    async function doTheWork(): Promise<void> {
      try {
        validateRiskIdAndUser();

        await addNewControls();

        await linkSelectedControls();

        if (onActionCompleted) {
          await onActionCompleted();
        }
      } catch (e) {
        handleError(e);

        throw e;
      } finally {
        closeSuggestionsPanel();

        setAddingControls(false);
      }
    }

    const promise = doTheWork();

    addNotification({
      type: 'promise',
      promise,
      successMessage: <>{'Successfully added/linked controls'}</>,
      errorMessage: (
        <>{'Encountered an error whilst adding/linking the controls'}</>
      ),
    });

    await promise;
  }

  function selectedChanged(id: string, selected: boolean): void {
    if (selected) {
      if (!selectedIds.includes(id)) {
        setSelectedIds([...selectedIds, id]);
      }
    } else {
      if (selectedIds.includes(id)) {
        const index = selectedIds.indexOf(id);

        setSelectedIds(selectedIds.splice(index, 1));
      }
    }
  }

  function closeSuggestionsPanel(): void {
    reset();

    onClose();

    closeSidePanel();
  }

  const showResults = useMemo<boolean>(() => {
    return (
      !isJobInProgress &&
      !!result &&
      !!result.result &&
      result.result.suggestedControls.length > 0 &&
      !hasErrored &&
      !hasTimedOut
    );
  }, [isJobInProgress, result, hasErrored, hasTimedOut]);

  const showNoResults = useMemo<boolean>(() => {
    return (
      !isJobInProgress &&
      !!result &&
      !!result.result &&
      result.result.suggestedControls.length === 0 &&
      !hasErrored &&
      !hasTimedOut
    );
  }, [isJobInProgress, result, hasErrored, hasTimedOut]);

  const showErrorOrTimeout = useMemo<boolean>(() => {
    return hasErrored || hasTimedOut;
  }, [hasErrored, hasTimedOut]);

  return (
    <SidePanelContainer
      header={
        <AISidePanelHeader onClose={closeSuggestionsPanel}></AISidePanelHeader>
      }
      content={
        <div className={'overflow-y-scroll no-scrollbar'}>
          {isJobInProgress && <AISidePanelLoading></AISidePanelLoading>}
          {showResults && (
            <div className={'px-4 py-2 m-5'}>
              <h3 className={'ms-0 mt-0 mb-8'}>{'Suggested Controls'}</h3>
              <p>
                {'Based on your current risk profile, I recommend implementing the following ' +
                  'controls to strengthen your risk management framework:'}
              </p>
              <ul className={'list-none p-0 mt-9'}>
                {result?.result?.suggestedControls.map((control) => {
                  return (
                    <AISuggestedControlCard
                      key={control.controlId!}
                      id={control.controlId!}
                      title={control.title}
                      description={control.description}
                      date={toLocalDate(control.createdAtTimestamp)}
                      createdBy={control.createdByUser}
                      onCheckedChanged={selectedChanged}
                      confidenceScore={control.confidenceScore}
                      controlType={control.controlType}
                      isExisting={control.isExisting}
                    ></AISuggestedControlCard>
                  );
                })}
              </ul>
              <button
                className={
                  'flex flex-row items-center w-full justify-center text-sm rounded-lg border-none bg-magenta text-white py-3 drop-shadow-lg disabled:cursor-not-allowed disabled:bg-grey300 cursor-pointer'
                }
                onClick={addControls}
                disabled={!(selectedIds.length > 0) || addingControls}
              >
                <Plus width={'20px'} className={'mr-3'} /> {'Add Controls'}
              </button>
            </div>
          )}
          {showNoResults && (
            <div className={'px-4 py-2 m-5'}>
              <h3 className={'ms-0 mt-0 mb-8'}>
                {"Hello! I'm your assistant."}
              </h3>
              <p>
                {
                  'Your risk details and description do not contain enough information for me to generate a control for this risk. Please provide additional details, or use the AI assistant to generate a more complete description.'
                }
              </p>
              <p>
                <strong>
                  {'Here are a few things you might want to update:'}
                </strong>
              </p>
              <ul className={'p-2'}>
                <li>{'Risk title'}</li>
                <li>{'Risk description'}</li>
              </ul>
            </div>
          )}
          {showErrorOrTimeout && (
            <div className={'px-4 py-2 m-5'}>
              <h3 className={'ms-0 mt-0 mb-8'}>
                {"Hello! I'm your assistant."}
              </h3>
              <p>
                {
                  'Unfortunately, something has gone wrong whilst trying to produce some suggested controls.'
                }
              </p>
              <p>{'Please close the AI Suggestions and try again'}</p>
            </div>
          )}
        </div>
      }
    ></SidePanelContainer>
  );
};
